// Orchestrates one RTCPeerConnection per client against the Worker's
// `/api/realtime/*` proxy, following Cloudflare Realtime's single-connection
// model: this same PeerConnection both publishes this client's own tracks
// (`location: "local"`) and pulls every other participant's tracks
// (`location: "remote"`), renegotiating after each batch (BRIEF 6.1).
//
// Every operation that touches this pc's signaling state — publish, pull,
// unpublish, toggling original sound — runs through one NegotiationQueue,
// so two overlapping calls (a `pull()` for a just-joined participant firing
// while the initial `publishBatch()` is still awaiting its answer) never
// race on the same offer/answer round trip. That race is glare, and it was
// real: `useCallStore`'s room-message handler is fire-and-forget per
// message, so two "welcome"/"participantJoined" messages arriving close
// together used to negotiate concurrently on the same `pc`.
//
// Verified against Cloudflare's own reference client
// (github.com/cloudflare/realtime-examples, video-room/src/client/media.ts,
// fetched 2026-09-27) for the offer/answer ordering: a push always sends an
// offer and expects an answer back in the same response; a pull sends NO
// offer, and only needs to answer when the response's own
// `requiresImmediateRenegotiation` is true (the returned `sessionDescription`
// is then an OFFER from the SFU, not an answer to anything this client
// sent) — earlier code here treated any returned `sessionDescription` as
// something to answer, which is wrong when the SFU could add tracks without
// a new offer at all.

import {
  DEFAULT_SETTINGS,
  NegotiationQueue,
  WorkerRealtimeClient,
  applyOriginalSoundMungingToMids,
  type RTCSessionDescriptionLike,
  type SimulcastPreference,
} from "@breeze/core";

export type MediaKind = "audio" | "video" | "screen";

export interface RemoteTrackHandle {
  readonly kind: MediaKind;
  readonly track: MediaStreamTrack;
}

export interface CallSessionHandlers {
  readonly onRemoteTrack: (participantId: string, handle: RemoteTrackHandle) => void;
  readonly onConnectionStateChange: (state: RTCPeerConnectionState) => void;
}

export interface PublishItem {
  readonly track: MediaStreamTrack;
  readonly kind: MediaKind;
  readonly originalSound?: boolean;
}

export interface PublishedTrack {
  readonly kind: MediaKind;
  readonly trackName: string;
  readonly mid: string;
}

export interface PullRequest {
  readonly participantId: string;
  readonly kind: MediaKind;
  readonly remote: { readonly sessionId: string; readonly trackName: string };
  /** Cloudflare's rid for the simulcast layer to receive (video only). A
   * full-screen 1:1 tile wants the top layer; a small grid tile wants the
   * bottom one (BRIEF 7.1). Omit for audio, or when the publisher sent no
   * simulcast layers at all. */
  readonly preferredRid?: string;
  /** True when the participant we're pulling this AUDIO track from has
   * original sound on (`ParticipantSnapshot.originalSound`). The SFU is a
   * pass-through, so the far side's own encoder already carries stereo —
   * but this side's DECODER still needs stereo declared in what it sends
   * back (the created answer, or a self-initiated renegotiation), or
   * playback stays mono regardless of what was encoded. Ignored for
   * non-audio kinds. */
  readonly originalSound?: boolean;
}

interface InboundRtp {
  readonly mid?: string;
  readonly packetsReceived?: number;
  readonly totalAudioEnergy?: number;
  readonly totalSamplesDuration?: number;
  readonly framesDecoded?: number;
  readonly frameWidth?: number;
  readonly frameHeight?: number;
  readonly framesPerSecond?: number;
  readonly bytesReceived?: number;
  readonly packetsLost?: number;
  readonly freezeCount?: number;
}

/** One pulled track's running totals (`CallSession.mediaCounters`). */
export interface InboundCounter {
  readonly mid: string;
  readonly participantId: string;
  readonly kind: MediaKind;
  readonly trackId: string;
  readonly muted: boolean;
  readonly packets: number;
  /** Audio: the sum of level squared times seconds, and the seconds. */
  readonly energy: number;
  readonly energySecs: number;
  /** Video: frames decoded, and the size and rate arriving now. */
  readonly frames: number;
  readonly width: number | null;
  readonly height: number | null;
  readonly fps: number | null;
  /** Running totals for how good the picture is: bytes, packets lost and
   * freezes (QUEUE row 452). */
  readonly bytes: number;
  readonly lost: number;
  readonly freezes: number;
}

export interface MediaCounters {
  readonly at: number;
  /** The connection's state when sampled, kept with a "can't hear" call so
   * a lost call reads differently from a false alarm (QUEUE row 475). */
  readonly ice?: string;
  readonly conn?: string;
  readonly inbound: readonly InboundCounter[];
  readonly stray: readonly { readonly mid: string; readonly kind: string; readonly packets: number }[];
  readonly out: {
    readonly audioPackets: number;
    readonly videoFrames: number;
    readonly micEnergy: number | null;
    readonly micSecs: number | null;
    /** Camera bytes sent, all layers. */
    readonly videoBytes: number;
    /** The top camera layer being sent: its height, and why the browser is
     * holding it back ("bandwidth", "cpu", "none"). */
    readonly sendHeight: number | null;
    readonly limit: string | null;
    /** The browser's estimate of the uplink in bits per second, and the
     * round trip to the SFU in seconds. */
    readonly uplinkBps: number | null;
    readonly rtt: number | null;
  };
}

/** How long to wait before asking again for a track the SFU has not seen
 * packets for yet. About eight seconds in all: a publisher's first packets
 * normally arrive within a second of its push. */
const PULL_RETRY_DELAYS_MS: readonly number[] = [250, 500, 750, 1000, 1500, 2000, 2000];

/** A pull the SFU kept answering "not found" through every retry. The push
 * behind it was accepted, yet the SFU never registered that track (QUEUE row
 * 473: about one join in five, someone's audio could not be pulled by anyone
 * for the rest of the call). Carries which tracks, so the caller can ask the
 * publisher to push them again. */
export class TracksNotSendingError extends Error {
  readonly requests: readonly PullRequest[];
  constructor(requests: readonly PullRequest[]) {
    super(`Tracks never started sending: ${requests.map((r) => r.remote.trackName).join(", ")}`);
    this.name = "TracksNotSendingError";
    this.requests = requests;
  }
}

/** Cloudflare's simulcast rid names, in low-to-high order, mapped from the
 * settings module's own layer names so this file is the only place that
 * knows the wire names ("f"/"h"/"q") the SFU actually expects. */
const RID_BY_LAYER: Readonly<Record<"low" | "medium" | "high", string>> = {
  low: "q",
  medium: "h",
  high: "f",
};

function simulcastSendEncodings(): RTCRtpEncodingParameters[] {
  return DEFAULT_SETTINGS.video.simulcastLayers.map((layer) => ({
    rid: RID_BY_LAYER[layer.name],
    scaleResolutionDownBy: layer.scaleResolutionDownBy,
    maxBitrate: layer.maxBitrateBps,
    maxFramerate: layer.maxFramerate,
  }));
}

/** The rid a puller should ask for when `tileCount` remote cameras share
 * the screen. One person fills it and gets the top layer; two or three get
 * the middle one, which is sharp at half the screen; four or more get the
 * bottom (BRIEF 7.1). The old rule sent everyone past one to the bottom
 * layer, and a pull made while a rejoin briefly showed two tiles kept it
 * for the rest of the call (QUEUE row 340: Sukhjit fuzzy for the host,
 * sharp in the recording). */
export function preferredRidForTileCount(tileCount: number): string {
  if (tileCount <= 1) return RID_BY_LAYER.high;
  if (tileCount <= 3) return RID_BY_LAYER.medium;
  return RID_BY_LAYER.low;
}

/** Asks for `preferredRid`, and when the publisher is not sending that layer
 * (a phone on a weak uplink drops its top one), the best layer it is
 * sending instead of nothing. "f" < "h" < "q", so asciibetical order is
 * best first. */
function simulcastFor(preferredRid: string): SimulcastPreference {
  return { preferredRid, priorityOrdering: "asciibetical", ridNotAvailable: "asciibetical" };
}

export class CallSession {
  readonly pc: RTCPeerConnection;
  private sessionId: string | null = null;
  private readonly client: WorkerRealtimeClient;
  private readonly queue = new NegotiationQueue();
  private readonly midToKind = new Map<string, MediaKind>();
  private readonly midToParticipant = new Map<string, string>();
  /** The (sessionId, trackName) this mid was pulled from, kept so a stereo
   * retoggle (see `rePullStereoUnqueued`) can re-request the exact same
   * remote track after closing the mid it currently occupies. */
  private readonly midToRemote = new Map<string, { sessionId: string; trackName: string }>();
  /** The layer each pulled camera mid was last asked for, so a change in how
   * many people are on screen asks the SFU only for what differs. */
  private readonly midToRid = new Map<string, string>();

  constructor(
    private readonly handlers: CallSessionHandlers,
    iceServers: readonly RTCIceServer[],
  ) {
    this.client = new WorkerRealtimeClient({
      baseUrl: "",
      fetchImpl: (url, init) => fetch(url, init as RequestInit),
    });
    this.pc = new RTCPeerConnection({ iceServers: iceServers as RTCIceServer[] });
    this.pc.addEventListener("track", (event) => this.handleTrack(event));
    this.pc.addEventListener("connectionstatechange", () => {
      this.handlers.onConnectionStateChange(this.pc.connectionState);
    });
  }

  async start(): Promise<string> {
    const { sessionId } = await this.client.newSession();
    this.sessionId = sessionId;
    this.startedAt = Date.now();
    return sessionId;
  }

  private startedAt = 0;

  /** A session the SFU opened but nothing has used yet expires on its side
   * within about a minute: a guest who waited at the door that long was let
   * in, and the first push came back 502, leaving them on the call screen
   * with no media (QUEUE row 409). Before the first use, one older than
   * `maxAgeMs` is swapped for a fresh one; the RTCPeerConnection has no
   * transceivers yet, so nothing on it refers to the old id. */
  async refreshIfUnused(maxAgeMs: number): Promise<void> {
    if (this.pc.getTransceivers().length > 0 || this.pc.signalingState !== "stable") return;
    if (Date.now() - this.startedAt < maxAgeMs) return;
    await this.start();
  }

  get id(): string {
    if (!this.sessionId) throw new Error("CallSession has not started yet.");
    return this.sessionId;
  }

  /** Publishes one or more local tracks (e.g. this client's mic AND camera)
   * in a SINGLE offer/answer round trip. Two tracks published as two
   * separate negotiations each pay the full SDP round trip and each risk
   * landing while the other is still in flight; one batched offer avoids
   * both. */
  async publishBatch(items: readonly PublishItem[]): Promise<readonly PublishedTrack[]> {
    if (items.length === 0) return [];
    return this.queue.run(() => this.publishBatchUnqueued(items));
  }

  /** The work of `publishBatch`, callable from inside a job already running
   * on `this.queue` (the original-sound republish). */
  private async publishBatchUnqueued(items: readonly PublishItem[]): Promise<readonly PublishedTrack[]> {
    await this.waitForStable();
    const sessionId = this.id;

    const transceivers = items.map((item) => {
      const init: RTCRtpTransceiverInit = { direction: "sendonly" };
      if (item.kind === "video") init.sendEncodings = simulcastSendEncodings();
      // A share is capped at its settings bitrate, so on a weak uplink it
      // cannot take the whole estimate and starve the camera's layers.
      if (item.kind === "screen") init.sendEncodings = [{ maxBitrate: DEFAULT_SETTINGS.screenShare.maxBitrateBps }];
      return { item, transceiver: this.pc.addTransceiver(item.track, init) };
    });

    const offer = await this.pc.createOffer();
    const rawSdp = offer.sdp ?? "";
    // The offer declares stereo on the original-sound mic section only;
    // every other audio section, including ones this client receives on,
    // stays as it was. The answer below is what actually sets the encoder.
    // A new transceiver has no `mid` until the offer is applied, so its
    // section is found in the offer text: an audio section whose mid no
    // existing transceiver holds.
    const wantsStereo = items.some((i) => i.kind === "audio" && i.originalSound);
    const knownMids = new Set(this.pc.getTransceivers().map((t) => t.mid).filter((m): m is string => !!m));
    const newAudioMids = rawSdp
      .split(/(?=^m=)/m)
      .filter((section) => section.startsWith("m=audio"))
      .map((section) => /^a=mid:(\S+)/m.exec(section)?.[1] ?? "")
      .filter((mid) => mid && !knownMids.has(mid));
    const sdp = wantsStereo && newAudioMids.length > 0 ? this.mungeForOriginalSoundMids(rawSdp, newAudioMids, true) : rawSdp;
    await this.pc.setLocalDescription({ type: "offer", sdp });

    const published: PublishedTrack[] = [];
    for (const { item, transceiver } of transceivers) {
      const mid = transceiver.mid ?? "";
      if (!mid) continue;
      const trackName = `${item.kind}-${crypto.randomUUID()}`;
      this.midToKind.set(mid, item.kind);
      published.push({ kind: item.kind, trackName, mid });
    }

    const result = await this.client.pushTracks(
      sessionId,
      { type: "offer", sdp },
      published.map((p) => ({ location: "local", mid: p.mid, trackName: p.trackName })),
    );
    // A push always offers, so the SFU always answers in the same
    // response — there is nothing further to renegotiate here.
    if (result.sessionDescription) {
      // What makes this side's ENCODER send stereo at a real bitrate is the
      // far side's answer declaring it (stereo=1, maxaveragebitrate): the
      // offer only says what this side can receive. The SFU's answer does
      // not echo those params, so they are written into it here, for the
      // original-sound audio mids only.
      const stereoMids = transceivers
        .filter(({ item }) => item.kind === "audio" && item.originalSound)
        .map(({ transceiver }) => transceiver.mid ?? "")
        .filter(Boolean);
      const answerSdp =
        stereoMids.length > 0
          ? this.mungeForOriginalSoundMids(result.sessionDescription.sdp, stereoMids, true)
          : result.sessionDescription.sdp;
      await this.pc.setRemoteDescription({ type: result.sessionDescription.type, sdp: answerSdp });
    }
    return published;
  }

  /** Publishes a single local track — the common case for a track added
   * mid-call (screen share), where batching with anything else would mean
   * waiting on an unrelated negotiation. Returns the transceiver's mid
   * alongside the trackName so the caller can `unpublish(mid)` it later —
   * `mid` is the SDP media-section identifier `unpublish` actually looks
   * up by, NOT the MediaStreamTrack's own `.id` (a plain GUID that never
   * matches any transceiver). */
  async publish(track: MediaStreamTrack, kind: MediaKind): Promise<PublishedTrack> {
    const [published] = await this.publishBatch([{ track, kind }]);
    if (!published) throw new Error("The SFU did not assign a media section for that track.");
    return published;
  }

  /** Turns original sound on or off for this client's own mic by
   * republishing the audio track: close the published section, then push the
   * same MediaStreamTrack again with (or without) stereo declared in the
   * SFU's answer. Cloudflare's `/renegotiate` accepts only an ANSWER from the
   * client (an offer 406s), so re-offering in place, which this used to do,
   * never reached the SFU and left this connection stuck in
   * have-local-offer. A fresh push is the one supported way to change what
   * the encoder is told. Returns the new track (a new trackName, which the
   * caller announces with `publishTrack`), or null when no audio was
   * published. */
  async setOriginalSound(on: boolean): Promise<PublishedTrack | null> {
    return this.queue.run(async () => {
      const track = this.ownSender("audio")?.track ?? null;
      if (!track) return null;
      // Music wants the mic unprocessed: no echo cancellation, noise
      // suppression or gain control. Best-effort; a browser that cannot
      // change these on a live track keeps its capture settings.
      try {
        await track.applyConstraints({ echoCancellation: !on, noiseSuppression: !on, autoGainControl: !on });
      } catch {
        // Keep going: the stereo encode below still applies.
      }
      return this.republishUnqueued("audio", on);
    });
  }

  /** Pushes this connection's own mic or camera again under a new track
   * name, for a track the SFU accepted and then never registered: everyone
   * else's pull of it is refused as not found (QUEUE row 473). Returns the
   * new track for the caller to announce with `publishTrack`, or null when
   * nothing of that kind is published. */
  async republish(kind: "audio" | "video", originalSound = false): Promise<PublishedTrack | null> {
    return this.queue.run(() => this.republishUnqueued(kind, originalSound));
  }

  private async republishUnqueued(kind: "audio" | "video", originalSound: boolean): Promise<PublishedTrack | null> {
    const own = this.ownSender(kind);
    const track = own?.track;
    if (!own || !track) return null;
    const { mid, sender } = own;
    // Retire the old section with removeTrack (it goes inactive), never
    // transceiver.stop(): the mic is this connection's FIRST media
    // section, the BUNDLE tag, and rejecting it left every section added
    // afterwards with no working transport (the new sender sent zero
    // bytes, and everyone else's pull of it failed as not found).
    this.pc.removeTrack(sender);
    this.midToKind.delete(mid);
    try {
      await this.client.closeTracks(this.id, [mid], true);
    } catch (err) {
      // The SFU frees it on its own timeout; the new publish below does
      // not depend on it.
      console.error(`Failed to close the old ${kind} section ${mid}:`, err);
    }
    const [published] = await this.publishBatchUnqueued([{ track, kind, originalSound: kind === "audio" && originalSound }]);
    return published ?? null;
  }

  /** This connection's own published section of a kind, with its sender. */
  private ownSender(kind: "audio" | "video"): { mid: string; sender: RTCRtpSender; track: MediaStreamTrack | null } | null {
    const mid = [...this.midToKind.entries()].find(([m, k]) => k === kind && !this.midToParticipant.has(m))?.[0];
    const sender = mid ? this.pc.getTransceivers().find((t) => t.mid === mid)?.sender : undefined;
    return mid && sender ? { mid, sender, track: sender.track } : null;
  }

  /** Swaps the currently-published camera video track for a new one (a
   * front/back flip) without a fresh publish/renegotiate round trip —
   * `RTCRtpSender.replaceTrack` handles this on the existing transceiver and
   * changes no SDP, so it deliberately does NOT go through the negotiation
   * queue. */
  replaceVideoTrack(track: MediaStreamTrack): void {
    this.replaceTrack("video", track);
  }

  /** The same swap for either kind: a mic or camera opened again after a
   * phone call took the old one (QUEUE row 329). A sender whose track ended
   * still holds it, so it is found the same way. */
  replaceTrack(kind: "audio" | "video", track: MediaStreamTrack): void {
    for (const transceiver of this.pc.getTransceivers()) {
      const mid = transceiver.mid;
      if (mid && this.midToKind.get(mid) === kind && transceiver.sender.track) {
        void transceiver.sender.replaceTrack(track);
        return;
      }
    }
  }

  async unpublish(mid: string): Promise<void> {
    return this.queue.run(async () => {
      const sender = this.pc.getTransceivers().find((t) => t.mid === mid)?.sender;
      if (sender) this.pc.removeTrack(sender);
      this.midToKind.delete(mid);
      // force:true is required by Cloudflare's tracks/close schema; it means
      // "stop media flow now, no SDP exchange" — exactly what we want here,
      // since removeTrack above has already torn down the local sender.
      await this.client.closeTracks(this.id, [mid], true);
    });
  }

  /** Pulls one or more remote tracks in a SINGLE offer/answer round trip
   * (used for a batch of participants already in the room at join time, and
   * for a participant's own two tracks — audio and video — arriving in one
   * `participantUpdated`). The mid-to-participant map is populated from the
   * SFU's response BEFORE `setRemoteDescription` is ever called, because
   * setting the remote description is exactly what fires `ontrack` for the
   * new m= sections — reading the map after that point would be reading it
   * too late for the very first frame. */
  async pullMany(requests: readonly PullRequest[]): Promise<void> {
    if (requests.length === 0) return;
    return this.queue.run(() => this.pullManyUnqueued(requests));
  }

  /** The actual work of `pullMany`. Broken out so `rePullStereoUnqueued` can
   * re-pull a track it just closed without a second `queue.run` (which would
   * deadlock against the job that is already running). MUST be called from
   * inside a job already running on `this.queue`. */
  private async pullManyUnqueued(requests: readonly PullRequest[], attempt = 0): Promise<void> {
    if (requests.length === 0) return;
    await this.waitForStable();
    const result = await this.client.pullTracks(
      this.id,
      requests.map((r) => {
        const simulcast: SimulcastPreference | undefined =
          r.kind === "video" && r.preferredRid ? simulcastFor(r.preferredRid) : undefined;
        return {
          location: "remote",
          sessionId: r.remote.sessionId,
          trackName: r.remote.trackName,
          ...(simulcast ? { simulcast } : {}),
        };
      }),
    );

    // Map the SFU's per-track response back to (participant, kind) by
    // trackName, since the request order and the response order are not
    // guaranteed to match one-to-one once errors are mixed in.
    const byTrackName = new Map(requests.map((r) => [r.remote.trackName, r] as const));
    const stereoMids: string[] = [];
    const landed = new Map<string, PullRequest>();
    const notSendingYet: PullRequest[] = [];
    const refused: string[] = [];
    for (const t of result.tracks) {
      const req = byTrackName.get(t.trackName);
      if (!req) continue;
      const mid = t.mid;
      if (!mid) {
        // A track announced a moment before its first packets reach the SFU
        // (every publish races its own announcement) is refused as not
        // found. Skipping it silently left that participant's audio, video
        // or screen missing for the rest of the call.
        if (t.errorCode === "not_found_track_error") notSendingYet.push(req);
        else refused.push(`${t.trackName}: ${t.errorCode ?? "no mid"}`);
        continue;
      }
      this.midToKind.set(mid, req.kind);
      this.midToParticipant.set(mid, req.participantId);
      this.midToRemote.set(mid, req.remote);
      if (req.kind === "video" && req.preferredRid) this.midToRid.set(mid, req.preferredRid);
      landed.set(mid, req);
      if (req.kind === "audio" && req.originalSound) stereoMids.push(mid);
    }

    if (result.requiresImmediateRenegotiation && result.sessionDescription?.type === "offer") {
      await this.pc.setRemoteDescription(result.sessionDescription as RTCSessionDescriptionInit);
      // The SFU reuses a section this connection retired (the mic before an
      // original-sound republish, a stopped screen share) for the next track
      // it pulls. That transceiver was left inactive by removeTrack, so the
      // answer refused it and the new track arrived on a section nothing
      // plays: whoever turned original sound on stopped hearing the person
      // whose mic followed (QUEUE row 280).
      for (const mid of landed.keys()) {
        const t = this.pc.getTransceivers().find((x) => x.mid === mid);
        if (t && t.direction !== "recvonly" && t.direction !== "sendrecv") t.direction = "recvonly";
      }
      const answer = await this.pc.createAnswer();
      // The answer we send is OUR OWN declaration of how we'll receive
      // this audio — it's what actually configures this side's decoder
      // for stereo, regardless of what the far side's encoder already
      // sent (see the PullRequest.originalSound doc comment above).
      const answerSdp =
        stereoMids.length > 0
          ? this.mungeForOriginalSoundMids(answer.sdp ?? "", stereoMids, true)
          : answer.sdp ?? "";
      await this.pc.setLocalDescription({ type: "answer", sdp: answerSdp });
      const answerDesc: RTCSessionDescriptionLike = { type: "answer", sdp: answerSdp };
      await this.client.renegotiate(this.id, answerDesc);
    } else if (stereoMids.length > 0) {
      // requiresImmediateRenegotiation === false means the SFU added these
      // tracks onto m= sections that already exist and are already
      // negotiated (recvonly transceivers reused from an earlier pull) — so
      // there is no answer here to munge, and per Cloudflare's own schema
      // /renegotiate only ever accepts an ANSWER from the client (confirmed
      // against the live API: a client-sent offer 406s with "invalid_params
      // — sessionDescription.type=answer is expected"), so we cannot just
      // push a fresh offer of our own to declare stereo on these mids in
      // place. Force the SFU to actually renegotiate by closing the reused
      // section and re-pulling the same track fresh — that always creates a
      // brand-new recvonly transceiver, which always requires the SFU to
      // offer and thus always gives us an answer to munge, above.
      await this.rePullStereoUnqueued(stereoMids, true);
    }

    // A participant who republished a kind (a reconnect, an original-sound
    // toggle) had an older section for it; close that one now that the new
    // one has landed, not before, so a slow pull never leaves a gap.
    const replaced = [...this.midToParticipant.entries()]
      .filter(([mid, participantId]) => {
        if (landed.has(mid)) return false;
        const kind = this.midToKind.get(mid);
        return [...landed.values()].some((r) => r.participantId === participantId && r.kind === kind);
      })
      .map(([mid]) => mid);
    if (replaced.length > 0) await this.closeMidsUnqueued(replaced);

    if (notSendingYet.length > 0) {
      const delay = PULL_RETRY_DELAYS_MS[attempt];
      if (delay === undefined) {
        throw new TracksNotSendingError(notSendingYet);
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      await this.pullManyUnqueued(notSendingYet, attempt + 1);
    }
    if (refused.length > 0) throw new Error(`The SFU refused pulling ${refused.join("; ")}`);
  }

  /** Re-declares (or reverts) stereo support for a participant's ALREADY-
   * pulled audio track. Needed because toggling original sound never
   * changes the (sessionId, trackName) pair that `useCallStore`'s
   * pull-diffing dedupes on — so a participant flipping original sound on
   * or off after we've already pulled their audio would otherwise never be
   * re-negotiated at all. A no-op if we never actually pulled audio from
   * this participant. */
  /** Asks for `rid` on every pulled camera that is on another layer. Called
   * whenever the number of people on screen changes, so a call that began
   * with several tiles sharpens when it is down to one. Best-effort: a
   * failed update leaves the old layer playing and is retried on the next
   * change. */
  async setVideoLayer(rid: string): Promise<void> {
    return this.queue.run(async () => {
      if (!this.sessionId) return;
      const tracks = [...this.midToRid.entries()]
        .filter(([mid, current]) => current !== rid && this.midToKind.get(mid) === "video")
        .flatMap(([mid]) => {
          const remote = this.midToRemote.get(mid);
          return remote
            ? [{ location: "remote" as const, mid, sessionId: remote.sessionId, trackName: remote.trackName, simulcast: simulcastFor(rid) }]
            : [];
        });
      if (tracks.length === 0) return;
      try {
        await this.client.updateTracks(this.sessionId, tracks);
        for (const t of tracks) this.midToRid.set(t.mid, rid);
      } catch (err) {
        console.warn("[breeze] video layer update failed", err);
      }
    });
  }

  /** What each pulled camera is receiving right now, for the leaving report:
   * the layer asked for, and the size and rate actually arriving. */
  async inboundVideo(): Promise<{ participantId: string; rid: string | null; width: number | null; height: number | null; fps: number | null }[]> {
    const out: { participantId: string; rid: string | null; width: number | null; height: number | null; fps: number | null }[] = [];
    for (const t of this.pc.getTransceivers()) {
      const mid = t.mid;
      if (!mid || this.midToKind.get(mid) !== "video" || !this.midToParticipant.has(mid) || !t.receiver.track) continue;
      let width: number | null = null;
      let height: number | null = null;
      let fps: number | null = null;
      try {
        const stats = await t.receiver.getStats();
        stats.forEach((r: { type?: string; frameWidth?: number; frameHeight?: number; framesPerSecond?: number }) => {
          if (r.type !== "inbound-rtp") return;
          width = r.frameWidth ?? width;
          height = r.frameHeight ?? height;
          fps = r.framesPerSecond ?? fps;
        });
      } catch {
        // A receiver torn down mid-read reports nothing; the row still names it.
      }
      out.push({ participantId: this.midToParticipant.get(mid) ?? "", rid: this.midToRid.get(mid) ?? null, width, height, fps });
    }
    return out;
  }

  /** Running totals for every pulled track and for what this side sends, for
   * the media health report (./mediaHealth.ts turns two of these into rates).
   * One getStats call. `stray` is a receive section no pulled track owns,
   * which should not exist; it counts as a sign of a pull gone wrong. */
  async mediaCounters(): Promise<MediaCounters> {
    const stats = await this.pc.getStats();
    const inboundByMid = new Map<string, InboundRtp>();
    let audioPackets = 0;
    let videoFrames = 0;
    let micEnergy: number | null = null;
    let micSecs: number | null = null;
    let videoBytes = 0;
    let sendHeight: number | null = null;
    let limit: string | null = null;
    let uplinkBps: number | null = null;
    let rtt: number | null = null;
    type Stat = InboundRtp & {
      type?: string;
      kind?: string;
      packetsSent?: number;
      framesSent?: number;
      bytesSent?: number;
      qualityLimitationReason?: string;
      nominated?: boolean;
      state?: string;
      availableOutgoingBitrate?: number;
      currentRoundTripTime?: number;
    };
    stats.forEach((r: Stat) => {
      if (r.type === "inbound-rtp" && r.mid) inboundByMid.set(r.mid, r);
      if (r.type === "outbound-rtp" && r.kind === "audio") audioPackets += r.packetsSent ?? 0;
      if (r.type === "outbound-rtp" && r.kind === "video") {
        videoFrames += r.framesSent ?? 0;
        videoBytes += r.bytesSent ?? 0;
        // The tallest layer sending is the top one; its reason is the one
        // that says why the picture going out is not as sharp as it could be.
        if (typeof r.frameHeight === "number" && r.frameHeight > (sendHeight ?? 0)) {
          sendHeight = r.frameHeight;
          limit = r.qualityLimitationReason ?? null;
        }
      }
      if (r.type === "candidate-pair" && r.nominated && r.state === "succeeded") {
        if (typeof r.availableOutgoingBitrate === "number") uplinkBps = r.availableOutgoingBitrate;
        if (typeof r.currentRoundTripTime === "number") rtt = r.currentRoundTripTime;
      }
      if (r.type === "media-source" && r.kind === "audio") {
        micEnergy = (micEnergy ?? 0) + (r.totalAudioEnergy ?? 0);
        micSecs = (micSecs ?? 0) + (r.totalSamplesDuration ?? 0);
      }
    });
    const inbound: InboundCounter[] = [];
    const stray: { mid: string; kind: string; packets: number }[] = [];
    for (const t of this.pc.getTransceivers()) {
      const mid = t.mid;
      const dir = t.currentDirection;
      if (!mid || !t.receiver.track || t.receiver.track.readyState === "ended") continue;
      if (dir !== "recvonly" && dir !== "sendrecv") continue;
      const r = inboundByMid.get(mid);
      const participantId = this.midToParticipant.get(mid);
      const kind = this.midToKind.get(mid);
      if (!participantId || !kind) {
        stray.push({ mid, kind: t.receiver.track.kind, packets: r?.packetsReceived ?? 0 });
        continue;
      }
      inbound.push({
        mid,
        participantId,
        kind,
        trackId: t.receiver.track.id,
        muted: t.receiver.track.muted,
        packets: r?.packetsReceived ?? 0,
        energy: r?.totalAudioEnergy ?? 0,
        energySecs: r?.totalSamplesDuration ?? 0,
        frames: r?.framesDecoded ?? 0,
        width: r?.frameWidth ?? null,
        height: r?.frameHeight ?? null,
        fps: r?.framesPerSecond ?? null,
        bytes: r?.bytesReceived ?? 0,
        lost: r?.packetsLost ?? 0,
        freezes: r?.freezeCount ?? 0,
      });
    }
    return { at: Date.now(), ice: this.pc.iceConnectionState, conn: this.pc.connectionState, inbound, stray, out: { audioPackets, videoFrames, micEnergy, micSecs, videoBytes, sendHeight, limit, uplinkBps, rtt } };
  }

  /** Closes what this side receives of one person's audio or camera and
   * pulls the same track again, which is what a leave and rejoin did for
   * someone who had gone silent or frozen (QUEUE row 413). */
  async rePull(participantId: string, kind: "audio" | "video"): Promise<void> {
    return this.queue.run(async () => {
      const mids = [...this.midToParticipant.entries()]
        .filter(([mid, p]) => p === participantId && this.midToKind.get(mid) === kind)
        .map(([mid]) => mid);
      const mid = mids[mids.length - 1];
      const remote = mid ? this.midToRemote.get(mid) : undefined;
      if (!remote) return;
      // Pulled fresh first: pullManyUnqueued closes the older section once the
      // new one has landed, so a slow pull never leaves a gap.
      await this.pullManyUnqueued([
        { participantId, kind, remote, ...(kind === "video" && mid && this.midToRid.get(mid) ? { preferredRid: this.midToRid.get(mid) } : {}) },
      ]);
    });
  }

  async setStereoForParticipant(participantId: string, on: boolean): Promise<void> {
    const mid = [...this.midToParticipant.entries()].find(
      ([m, p]) => p === participantId && this.midToKind.get(m) === "audio",
    )?.[0];
    if (!mid) return;
    return this.queue.run(() => this.rePullStereoUnqueued([mid], on));
  }

  /** Closes each named mid (force:true — the section is about to be
   * re-pulled fresh, so there's nothing to negotiate a clean handoff for)
   * and immediately re-pulls the same (sessionId, trackName) each one was
   * serving, this time asking for `on`'s stereo declaration. This is the
   * only way to actually change a receive section's declared codec params
   * after the fact: Cloudflare's `/renegotiate` only ever accepts an
   * ANSWER from the client (see the long comment in `pullManyUnqueued`), so
   * there is no in-place "just renegotiate this" call — a fresh pull is the
   * one operation that reliably makes the SFU offer again. MUST be called
   * from inside a job already running on `this.queue`. */
  private async rePullStereoUnqueued(mids: readonly string[], on: boolean): Promise<void> {
    const requests: PullRequest[] = [];
    for (const mid of mids) {
      const participantId = this.midToParticipant.get(mid);
      const kind = this.midToKind.get(mid);
      const remote = this.midToRemote.get(mid);
      if (!participantId || kind !== "audio" || !remote) continue;
      requests.push({ participantId, kind, remote, originalSound: on });
    }
    if (requests.length === 0) return;
    await this.closeMidsUnqueued(mids);
    await this.pullManyUnqueued(requests);
  }

  /** Pulls a single remote participant's single track. A thin wrapper over
   * `pullMany` for the common one-track case (a participant who just
   * published their screen share, say). */
  async pull(
    participantId: string,
    remote: { readonly sessionId: string; readonly trackName: string },
    kind: MediaKind,
    preferredRid?: string,
  ): Promise<void> {
    return this.pullMany([{ participantId, kind, remote, preferredRid }]);
  }

  close(): void {
    this.pc.close();
  }

  /** True while the media connection is up and carrying traffic, which is
   * when a lost signaling socket can be reopened without touching it. */
  get mediaUp(): boolean {
    const ice = this.pc.iceConnectionState;
    return this.pc.connectionState === "connected" && (ice === "connected" || ice === "completed");
  }

  /** Stops every sender from sending, for a session about to publish its
   * tracks again under new names: the old ones would otherwise keep going up
   * to the SFU beside the new ones. */
  async stopSending(): Promise<void> {
    await Promise.all(this.pc.getSenders().map((s) => (s.track ? s.replaceTrack(null).catch(() => {}) : Promise.resolve())));
  }

  /** The original-sound Opus settings, scoped to specific mids, with
   * `stereo` set explicitly: `on: false` reverts a receive section to mono,
   * which matters because a pull-side section can outlive several
   * original-sound toggles from the same participant over one call. */
  private mungeForOriginalSoundMids(sdp: string, mids: readonly string[], on: boolean): string {
    return applyOriginalSoundMungingToMids(sdp, mids, {
      bitrateBps: DEFAULT_SETTINGS.originalSound.opusBitrateBps,
      stereo: on,
      dtx: DEFAULT_SETTINGS.originalSound.dtx,
      fec: DEFAULT_SETTINGS.originalSound.fec,
    });
  }

  /** Stops every receive transceiver this session is holding for
   * `participantId` and asks the SFU to close the matching tracks, so a
   * long call doesn't accumulate dead recvonly transceivers for every
   * participant who has ever left (BRIEF step 6). Safe to call for a
   * participant we never actually pulled anything from — a no-op. */
  async closeReceivedTracksFor(participantId: string): Promise<void> {
    const mids = [...this.midToParticipant.entries()]
      .filter(([, p]) => p === participantId)
      .map(([mid]) => mid);
    if (mids.length === 0) return;
    return this.queue.run(() => this.closeMidsUnqueued(mids));
  }

  /** Stops each mid's transceiver locally and best-effort tells the SFU to
   * close it (force:true — no SDP exchange, matching Cloudflare's schema:
   * `force` is a required field on tracks/close, and `false` would need us
   * to supply our own offer and apply the returned answer, which is more
   * than either caller here needs). MUST be called from inside a job
   * already running on `this.queue`. */
  private async closeMidsUnqueued(mids: readonly string[]): Promise<void> {
    if (mids.length === 0) return;
    for (const mid of mids) {
      const transceiver = this.pc.getTransceivers().find((t) => t.mid === mid);
      try {
        transceiver?.stop();
      } catch {
        // Some browsers throw if the transceiver was already stopped —
        // e.g. the connection itself already tore it down. Either way
        // the outcome we want (no more media on this mid) already holds.
      }
      this.midToKind.delete(mid);
      this.midToParticipant.delete(mid);
      this.midToRemote.delete(mid);
      this.midToRid.delete(mid);
    }
    try {
      await this.client.closeTracks(this.id, mids, true);
    } catch (err) {
      // Best-effort: whatever this mid was for is already gone (a departed
      // participant, or a section we're about to re-pull), so a failure
      // here just means the SFU frees the track slot on its own timeout
      // instead of immediately — never let it block the local cleanup
      // above, which is what actually stops this side receiving.
      console.error(`Failed to close SFU tracks for mids ${mids.join(", ")}:`, err);
    }
  }

  /** `createOffer`/`setRemoteDescription` are only valid from a "stable"
   * signaling state. Every entry point already runs inside this session's
   * one `NegotiationQueue`, so in practice this never actually waits — it
   * is a correctness assertion (fail fast and loud instead of silently
   * corrupting SDP state) more than a real wait. */
  private async waitForStable(): Promise<void> {
    if (this.pc.signalingState === "stable") return;
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pc.removeEventListener("signalingstatechange", onChange);
        reject(new Error("WebRTC signaling did not return to stable in time."));
      }, 10_000);
      const onChange = (): void => {
        if (this.pc.signalingState !== "stable") return;
        clearTimeout(timeout);
        this.pc.removeEventListener("signalingstatechange", onChange);
        resolve();
      };
      this.pc.addEventListener("signalingstatechange", onChange);
    });
  }

  private handleTrack(event: RTCTrackEvent): void {
    const mid = event.transceiver.mid;
    if (!mid) return;
    const kind = this.midToKind.get(mid);
    const participantId = this.midToParticipant.get(mid);
    if (!kind || !participantId) return;
    this.handlers.onRemoteTrack(participantId, { kind, track: event.track });
  }
}

export async function fetchIceServers(): Promise<readonly RTCIceServer[]> {
  const client = new WorkerRealtimeClient({ baseUrl: "", fetchImpl: (url, init) => fetch(url, init as RequestInit) });
  try {
    const { available, iceServers } = await client.turnCredentials();
    if (!available) return [];
    return iceServers.map((s) => ({ urls: s.urls as string | string[], username: s.username, credential: s.credential }));
  } catch {
    // TURN is a NAT-traversal improvement, not a hard requirement — STUN
    // alone (the browser's default) still works for most calls.
    return [];
  }
}
